import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { classificationService } from '../../services/classificationService';
import { productService } from '../../services/productService';
import FamilyForm from '../../components/Classification/FamilyForm';
import ClassificationExcelToolbar from '../../components/Classification/ClassificationExcelToolbar';
import {
  ClassificationBulkDeleteModal,
  ClassificationBulkDeleteTd,
  ClassificationBulkDeleteTh,
  ClassificationBulkDeleteToolbar,
} from '../../components/Classification/ClassificationBulkDeleteUi';
import ConfirmModal from '../../components/Common/ConfirmModal';
import Toast from '../../components/Common/Toast';
import MutateGate from '../../components/Common/MutateGate';
import { useCanMutate } from '../../hooks/useCanMutate';
import { useClassificationBulkDelete } from '../../hooks/useClassificationBulkDelete';
import { useClassificationHierarchyForBulk } from '../../hooks/useClassificationHierarchyForBulk';
import { buildCountMap } from '../../utils/classificationBulkDelete';

const Families = () => {
  const canMutate = useCanMutate();
  const [searchParams] = useSearchParams();
  const subcategoryIdFromUrl = searchParams.get('subcategory');
  const categoryIdFromUrl = searchParams.get('category');

  const [families, setFamilies] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(categoryIdFromUrl || '');
  const [selectedSubcategory, setSelectedSubcategory] = useState(subcategoryIdFromUrl || '');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingFamily, setEditingFamily] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [familyToDelete, setFamilyToDelete] = useState(null);
  const [toast, setToast] = useState({ isOpen: false, message: '', type: 'info' });
  const hasLoaded = useRef(false);
  const previousFilters = useRef({ category: selectedCategory, subcategory: selectedSubcategory });
  const { hierarchy: fullHierarchy, reloadHierarchy } = useClassificationHierarchyForBulk(
    classificationService,
    families.length
  );
  const [productsByFamily, setProductsByFamily] = useState({});

  useEffect(() => {
    if (!hasLoaded.current) {
      hasLoaded.current = true;
      previousFilters.current = { category: selectedCategory, subcategory: selectedSubcategory };
      loadData();
    }
  }, []);

  useEffect(() => {
    if (!hasLoaded.current) return;
    const prev = previousFilters.current;
    if (prev.category === selectedCategory && prev.subcategory === selectedSubcategory) return;
    previousFilters.current = { category: selectedCategory, subcategory: selectedSubcategory };
    loadFamilies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategory, selectedSubcategory]);

  useEffect(() => {
    let cancelled = false;
    productService
      .getProducts({})
      .then((res) => {
        if (cancelled) return;
        if (res.success && Array.isArray(res.data)) {
          setProductsByFamily(buildCountMap(res.data, 'family_id'));
        } else {
          setProductsByFamily({});
        }
      })
      .catch(() => {
        if (!cancelled) setProductsByFamily({});
      });
    return () => {
      cancelled = true;
    };
  }, [families.length]);

  const loadData = async () => {
    await loadCategories();
    await loadSubcategories();
    await loadFamilies();
  };

  const loadCategories = async () => {
    try {
      const response = await classificationService.getCategories({ active_only: false });
      if (response.success) {
        setCategories(response.data);
      }
    } catch (error) {
      console.error('❌ [Families] Error al cargar categorías:', error);
    }
  };

  const loadSubcategories = async () => {
    try {
      console.log('📁 [Families] Cargando subcategorías...');
      const response = await classificationService.getSubcategories({ active_only: false });

      if (response.success) {
        const data = response.data;
        setSubcategories(data);
        if (subcategoryIdFromUrl && !categoryIdFromUrl) {
          const sub = data.find((s) => String(s.id) === String(subcategoryIdFromUrl));
          if (sub) {
            setSelectedCategory(String(sub.category_id));
          }
        }
        console.log('✅ [Families] Subcategorías cargadas:', data.length);
      }
    } catch (error) {
      console.error('❌ [Families] Error al cargar subcategorías:', error);
    }
  };

  const loadFamilies = async () => {
    try {
      setLoading(true);
      console.log('📁 [Families] Cargando familias...');
      console.log('🌐 [Families] Filtros categoría:', selectedCategory || 'Todas', 'subcategoría:', selectedSubcategory || 'Todas');

      const params = {};
      if (selectedSubcategory) {
        params.subcategory_id = selectedSubcategory;
      } else if (selectedCategory) {
        params.category_id = selectedCategory;
      }
      
      const response = await classificationService.getFamilies(params);
      
      console.log('✅ [Families] Familias cargadas exitosamente');
      console.log('📊 [Families] Respuesta:', response);
      console.log('📦 [Families] Total de familias:', response.data?.length || 0);
      
      if (response.success) {
        setFamilies(response.data);
        console.log('💾 [Families] Familias guardadas en estado');
      } else {
        console.warn('⚠️ [Families] Respuesta sin éxito:', response);
      }
    } catch (error) {
      console.error('❌ [Families] Error al cargar familias:', error);
      console.error('📊 [Families] Respuesta del servidor:', error.response?.data);
    } finally {
      setLoading(false);
      console.log('🏁 [Families] Proceso de carga finalizado');
    }
  };

  const handleDeleteClick = (family) => {
    setFamilyToDelete(family);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!familyToDelete) return;

    try {
      console.log('🗑️ [Families] Eliminando familia:', familyToDelete.id);
      const response = await classificationService.deleteFamily(familyToDelete.id);
      
      if (response.success) {
        console.log('✅ [Families] Familia eliminada exitosamente');
        setIsDeleteModalOpen(false);
        setFamilyToDelete(null);
        setToast({
          isOpen: true,
          message: 'Familia eliminada exitosamente',
          type: 'success'
        });
        loadFamilies(); // Recargar la lista
      } else {
        console.error('❌ [Families] Error al eliminar:', response.message);
        setIsDeleteModalOpen(false);
        setToast({
          isOpen: true,
          message: response.message || 'Error al eliminar la familia',
          type: 'error'
        });
      }
    } catch (error) {
      console.error('❌ [Families] Error al eliminar:', error);
      setIsDeleteModalOpen(false);
      setToast({
        isOpen: true,
        message: error.response?.data?.message || 'Error al eliminar la familia',
        type: 'error'
      });
    }
  };

  const handleFormSuccess = () => {
    console.log('✅ [Families] Formulario guardado exitosamente, recargando lista...');
    loadFamilies();
  };

  const filteredFamilies = families.filter(family => {
    const matchesSearch = family.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      family.code.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  const bulk = useClassificationBulkDelete({
    variant: 'families',
    rows: filteredFamilies,
    hierarchy: fullHierarchy,
    lowerLevelCounts: productsByFamily,
    blockRecetaOrigin: true,
    deleteItem: (id) => classificationService.deleteFamily(id),
    onDeleted: async () => {
      await loadFamilies();
      await reloadHierarchy();
    },
    setToast,
    enabled: canMutate,
  });

  const subcategoriesForCategory = selectedCategory
    ? subcategories.filter((s) => String(s.category_id) === String(selectedCategory))
    : subcategories;

  const selectedCategoryName = selectedCategory
    ? categories.find((c) => String(c.id) === String(selectedCategory))?.name
    : null;

  const selectedSubcategoryName = selectedSubcategory
    ? subcategories.find((s) => String(s.id) === String(selectedSubcategory))?.name
    : null;

  const selectedSubcategoryHasRecetaOrigin = useMemo(() => {
    if (!selectedSubcategory) return false;
    const s = subcategories.find((x) => String(x.id) === String(selectedSubcategory));
    return Boolean(s?.receta_subcategory_id);
  }, [subcategories, selectedSubcategory]);

  const isFromRecetasFam = (row) =>
    row?.receta_family_id != null && row.receta_family_id !== '';

  const handleCategoryFilterChange = (e) => {
    const v = e.target.value;
    setSelectedCategory(v);
    setSelectedSubcategory((current) => {
      if (!v || !current) return '';
      const sub = subcategories.find((s) => String(s.id) === String(current));
      if (!sub || String(sub.category_id) !== String(v)) return '';
      return current;
    });
  };

  // Mostrar loading solo cuando está cargando por primera vez
  if (loading && !hasLoaded.current) {
    return (
      <div className="p-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-center items-center h-64">
            <div className="text-lg text-gray-600">Cargando familias...</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="p-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-6">
            <div className="flex flex-col gap-4 lg:flex-row lg:justify-between lg:items-start mb-4">
              <div>
                <h1 className="text-3xl font-bold text-gray-800">Familias</h1>
                <p className="text-gray-600 mt-1">Nivel 3 - Clasificación de Productos</p>
                {(selectedCategory || selectedSubcategory) && (
                  <p className="text-sm text-teal-600 mt-1">
                    {selectedCategory && (
                      <>
                        Categoría:{' '}
                        <span className="font-medium">
                          {selectedCategoryName || selectedCategory}
                        </span>
                      </>
                    )}
                    {selectedCategory && selectedSubcategory && ' · '}
                    {selectedSubcategory && (
                      <>
                        Subcategoría:{' '}
                        <span className="font-medium">
                          {selectedSubcategoryName || selectedSubcategory}
                        </span>
                      </>
                    )}
                  </p>
                )}
              </div>
              <div className="flex flex-col items-stretch gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:justify-end">
                <ClassificationExcelToolbar
                  variant="families"
                  onImported={loadFamilies}
                  setToast={setToast}
                  exportRows={filteredFamilies}
                  exportContext={{ categories, subcategories }}
                />
                <MutateGate>
                  <button
                    type="button"
                    disabled={selectedSubcategoryHasRecetaOrigin}
                    title={
                      selectedSubcategoryHasRecetaOrigin
                        ? 'Las familias de esta subcategoría se gestionan desde Recetas'
                        : undefined
                    }
                    onClick={() => {
                      setEditingFamily(null);
                      setIsFormOpen(true);
                    }}
                    className={`px-4 py-2 rounded-md font-medium transition-colors text-white ${
                      selectedSubcategoryHasRecetaOrigin
                        ? 'bg-gray-400 cursor-not-allowed'
                        : 'bg-teal-600 hover:bg-teal-700'
                    }`}
                  >
                    + Nueva Familia
                  </button>
                </MutateGate>
              </div>
            </div>

            {/* Breadcrumb */}
            <nav className="text-sm text-gray-600 mb-4">
              <Link to="/dashboard" className="hover:text-teal-600">Dashboard</Link>
              <span className="mx-2">/</span>
              <Link to="/classification" className="hover:text-teal-600">Clasificación</Link>
              <span className="mx-2">/</span>
              <span className="text-gray-800">Familias</span>
            </nav>

            {/* Filtros en una sola línea; scroll horizontal si el viewport es muy estrecho */}
            <div className="flex flex-nowrap items-end gap-3 mb-4 w-full overflow-x-auto pb-0.5">
              <div className="flex flex-col gap-1 flex-shrink-0 w-44 sm:w-48">
                <label htmlFor="families-filter-category" className="text-xs font-medium text-gray-600 whitespace-nowrap">
                  Categoría
                </label>
                <select
                  id="families-filter-category"
                  value={selectedCategory}
                  onChange={handleCategoryFilterChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                >
                  <option value="">Todas las categorías</option>
                  {categories.map((cat) => (
                    <option key={cat.id} value={cat.id}>
                      {cat.code} — {cat.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex flex-col gap-1 flex-shrink-0 w-52 sm:w-64">
                <label htmlFor="families-filter-subcategory" className="text-xs font-medium text-gray-600 whitespace-nowrap">
                  Subcategoría
                </label>
                <select
                  id="families-filter-subcategory"
                  value={selectedSubcategory}
                  onChange={(e) => setSelectedSubcategory(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 disabled:bg-gray-100 disabled:text-gray-500 text-sm"
                  disabled={Boolean(selectedCategory) && subcategoriesForCategory.length === 0}
                >
                  <option value="">
                    {selectedCategory ? 'Todas en esta categoría' : 'Todas las subcategorías'}
                  </option>
                  {subcategoriesForCategory.map((sub) => (
                    <option key={sub.id} value={sub.id}>
                      {sub.code} — {sub.name}
                      {!selectedCategory && sub.category
                        ? ` (${sub.category.code} — ${sub.category.name})`
                        : ''}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex flex-col gap-1 flex-1 min-w-[12rem] basis-0 min-h-0">
                <label htmlFor="families-search" className="text-xs font-medium text-gray-600 whitespace-nowrap">
                  Buscar
                </label>
                <input
                  id="families-search"
                  type="text"
                  placeholder="Buscar familias..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full min-w-[12rem] px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                />
              </div>
            </div>
          </div>

          {/* Stats */}
          <div className="mb-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white rounded-lg shadow-md p-4">
              <div className="text-sm text-gray-600">Total Familias</div>
              <div className="text-2xl font-bold text-gray-800">{filteredFamilies.length}</div>
            </div>
            <div className="bg-white rounded-lg shadow-md p-4">
              <div className="text-sm text-gray-600">Familias Activas</div>
              <div className="text-2xl font-bold text-green-600">
                {filteredFamilies.filter(f => f.is_active).length}
              </div>
            </div>
            <div className="bg-white rounded-lg shadow-md p-4">
              <div className="text-sm text-gray-600">Familias Inactivas</div>
              <div className="text-2xl font-bold text-red-600">
                {filteredFamilies.filter(f => !f.is_active).length}
              </div>
            </div>
          </div>

          {/* Table: scroll horizontal + columna Acciones fija; texto largo hace wrap */}
          <ClassificationBulkDeleteToolbar bulk={bulk} />

          <div className="bg-white rounded-lg shadow-md overflow-x-auto">
            {filteredFamilies.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                {searchTerm
                  ? 'No se encontraron familias con ese criterio'
                  : selectedSubcategory || selectedCategory
                    ? 'No hay familias para los filtros seleccionados'
                    : 'No hay familias registradas'}
              </div>
            ) : (
              <table className="w-full min-w-[min(100%,52rem)] divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <ClassificationBulkDeleteTh bulk={bulk} />
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                      Código
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider max-w-[11rem]">
                      Nombre
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider max-w-[12rem]">
                      Subcategoría
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider max-w-[14rem]">
                      Categoría
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider max-w-[12rem]">
                      Descripción
                    </th>
                    <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                      Estado
                    </th>
                    <th className="sticky right-0 z-20 px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50 border-l border-gray-200 shadow-[-8px_0_12px_-6px_rgba(0,0,0,0.12)] whitespace-nowrap">
                      Acciones
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredFamilies.map((family) => (
                    <tr key={family.id} className="group hover:bg-gray-50">
                      <ClassificationBulkDeleteTd bulk={bulk} row={family} />
                      <td className="px-3 py-3 align-top whitespace-nowrap">
                        <span className="text-sm font-medium text-gray-900">{family.code}</span>
                      </td>
                      <td className="px-3 py-3 align-top max-w-[11rem] break-words">
                        <span className="text-sm text-gray-900 inline-flex items-center gap-2 flex-wrap">
                          {family.name}
                          {isFromRecetasFam(family) ? (
                            <span
                              className="text-xs font-medium px-2 py-0.5 rounded-full bg-amber-100 text-amber-900 border border-amber-200"
                              title="Sincronizada desde recetas; no se puede eliminar aquí"
                            >
                              Recetas
                            </span>
                          ) : null}
                        </span>
                      </td>
                      <td className="px-3 py-3 align-top max-w-[12rem] break-words">
                        <Link
                          to={`/classification/subcategories?category=${family.subcategory?.category_id}`}
                          className="text-sm text-teal-600 hover:text-teal-900 break-words"
                        >
                          {family.subcategory?.code || ''} - {family.subcategory?.name || 'N/A'}
                        </Link>
                      </td>
                      <td className="px-3 py-3 align-top max-w-[14rem] break-words">
                        <Link
                          to={`/classification/categories`}
                          className="text-sm text-gray-600 hover:text-teal-600 break-words"
                        >
                          {family.subcategory?.category?.code || ''} - {family.subcategory?.category?.name || 'N/A'}
                        </Link>
                      </td>
                      <td className="px-3 py-3 align-top max-w-[12rem] break-words">
                        <span className="text-sm text-gray-500">{family.description || '-'}</span>
                      </td>
                      <td className="px-2 py-3 align-top whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs rounded-full ${
                          family.is_active
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {family.is_active ? 'Activo' : 'Inactivo'}
                        </span>
                      </td>
                      <td className="sticky right-0 z-10 px-3 py-3 align-top text-sm font-medium bg-white border-l border-gray-200 shadow-[-8px_0_12px_-6px_rgba(0,0,0,0.12)] group-hover:bg-gray-50 whitespace-nowrap">
                        <div className="flex flex-col gap-1">
                          {canMutate ? (
                            <>
                              <button
                                type="button"
                                onClick={() => {
                                  setEditingFamily(family);
                                  setIsFormOpen(true);
                                }}
                                className="text-purple-600 hover:text-purple-900 text-left"
                              >
                                Editar
                              </button>
                              {isFromRecetasFam(family) ? (
                                <span
                                  className="text-xs text-gray-500 text-left"
                                  title="Elimine o gestione esta familia desde Producción → Clasificación de recetas"
                                >
                                  Sin eliminar (recetas)
                                </span>
                              ) : (
                                <button
                                  type="button"
                                  onClick={() => handleDeleteClick(family)}
                                  className="text-red-600 hover:text-red-900 text-left"
                                >
                                  Eliminar
                                </button>
                              )}
                            </>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* Modal de Formulario */}
        <FamilyForm
          isOpen={isFormOpen}
          onClose={() => {
            setIsFormOpen(false);
            setEditingFamily(null);
          }}
          family={editingFamily}
          onSuccess={handleFormSuccess}
        />

        <ClassificationBulkDeleteModal bulk={bulk} />

        {/* Modal de Confirmación de Eliminación */}
        <ConfirmModal
          isOpen={isDeleteModalOpen}
          onClose={() => {
            setIsDeleteModalOpen(false);
            setFamilyToDelete(null);
          }}
          onConfirm={handleDeleteConfirm}
          title="Eliminar Familia"
          message={`¿Estás seguro de que deseas eliminar la familia "${familyToDelete?.name}" (${familyToDelete?.code})? Esta acción no se puede deshacer.`}
          confirmText="Eliminar"
          cancelText="Cancelar"
          type="danger"
        />

        {/* Toast de notificaciones */}
        <Toast
          isOpen={toast.isOpen}
          message={toast.message}
          type={toast.type}
          onClose={() => setToast({ ...toast, isOpen: false })}
        />
      </div>
    </div>
  );
};

export default Families;
